import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { findColor, colorToHex } from "@/constants/colors";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

const BASE = process.env.REACT_APP_BACKEND_URL;

export function useColorSwatches() {
  return useQuery({
    queryKey: ["color-swatches"],
    queryFn: async () => (await api.get("/color-swatches")).data,
    staleTime: 10 * 60 * 1000,
  });
}

const norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

// Match an order's color to a swatch file: exact name → canonical color name
// → any alias of the canonical color (e.g. "Rose Gold" ↔ "Premium Pink.png").
export function swatchFor(colorName, swatches) {
  if (!colorName || !Array.isArray(swatches) || swatches.length === 0) return null;
  const byName = new Map(swatches.map((s) => [norm(s.name), s]));
  const raw = norm(colorName);
  if (byName.has(raw)) return byName.get(raw);
  if (raw.includes("custom") && byName.has("custom color")) return byName.get("custom color");
  const c = findColor(colorName);
  if (!c) return null;
  if (byName.has(norm(c.name))) return byName.get(norm(c.name));
  for (const a of c.aliases || []) if (byName.has(norm(a))) return byName.get(norm(a));
  return null;
}

export const swatchUrl = (s) => `${BASE}/api/color-swatches/${s.file_id}/image`;

// Colour dot used in list rows — hovering it pops up the real cover photo.
export function ColorDot({ color, size = 18, testid }) {
  const { data } = useColorSwatches();
  const s = swatchFor(color, data);
  const name = findColor(color)?.name || color || "";
  const dot = (
    <span
      className="inline-block rounded-full border border-slate-300 shadow-sm ring-1 ring-black/5 cursor-help"
      style={{ width: size, height: size, background: colorToHex(color) }}
      title={s ? undefined : name}
      data-testid={testid}
    />
  );
  if (!s) return dot;
  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>{dot}</HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-auto p-2" data-testid={`${testid || "color-dot"}-preview`}>
        <img
          src={swatchUrl(s)}
          alt={s.name}
          className="h-44 w-auto max-w-[280px] object-contain rounded-md bg-white"
          loading="lazy"
        />
        <div className="mt-1.5 text-center text-[11px] font-mono uppercase tracking-widest text-slate-600">{name}</div>
      </HoverCardContent>
    </HoverCard>
  );
}

// Big photo of the correct cover color for the packing checklist "Color" step.
export function ColorSwatchImage({ color, className = "h-56", testid = "color-swatch" }) {
  const { data } = useColorSwatches();
  const s = swatchFor(color, data);
  if (!s) return null;
  return (
    <img
      src={swatchUrl(s)}
      alt={s.name}
      title={s.name}
      data-testid={testid}
      className={`${className} w-auto max-w-full object-contain rounded-lg border-2 border-slate-200 bg-white shadow-sm`}
    />
  );
}
